"use client";

import Link from "next/link";
import {
  useEffect,
  useRef,
  type ButtonHTMLAttributes,
  type ComponentProps,
  type ReactNode,
} from "react";
import { trackEvent } from "@/lib/analytics";
import type { CompanionAnalyticsEvent } from "@/lib/companion/analytics-events";

export function CompanionViewAnalytics({
  event,
}: {
  event: CompanionAnalyticsEvent;
}): null {
  const trackedRef = useRef<string | null>(null);

  useEffect(() => {
    const signature = JSON.stringify([event.name, event.properties]);
    if (trackedRef.current === signature) return;
    trackedRef.current = signature;
    trackEvent(event.name, event.properties);
  }, [event]);

  return null;
}

export type CompanionTrackedLinkProps = ComponentProps<typeof Link> & {
  event: CompanionAnalyticsEvent;
  children: ReactNode;
};

export function CompanionTrackedLink({
  event,
  onClick,
  children,
  ...props
}: CompanionTrackedLinkProps) {
  return (
    <Link
      {...props}
      onClick={(clickEvent) => {
        try {
          trackEvent(event.name, event.properties);
        } catch {
          // Analytics must never interrupt navigation.
        }
        onClick?.(clickEvent);
      }}
    >
      {children}
    </Link>
  );
}

export type CompanionTrackedButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  event: CompanionAnalyticsEvent;
};

export function CompanionTrackedButton({
  event,
  onClick,
  ...props
}: CompanionTrackedButtonProps) {
  return (
    <button
      {...props}
      onClick={(clickEvent) => {
        try {
          trackEvent(event.name, event.properties);
        } catch {
          // Analytics must never interrupt the server action.
        }
        onClick?.(clickEvent);
      }}
    />
  );
}
